// eslint-disable-next-line @typescript-eslint/naming-convention
import React from 'react';
import { api570SelectOptions } from '../../../customInspectionTypes/api570/api570SelectOptions';
import { defaultSelectOptions } from '../../../customInspectionTypes/default/defaultSelectOptions';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { InspectionStandardTypes } from '../../../utils/enums/InspectionStandardTypes';
import { indexToOddOrEven } from '../../../utils/indexToOddOrEven';
import { PanelButtonLink, PanelHeading, PanelLink, PanelResult, TableExpandButton } from '../../elements';
import { ConditionScoreLabel } from '../../utils/ConditionScoreLabel';
import { NullableFormattedDate } from '../../utils/NullableFormattedDate';
import { AssetsViewer } from '../Common/AssetsViewer';
import { ICategory } from '../Interfaces/ICategory';
import { IElement } from '../Interfaces/IElement';
import { IManifestation } from '../Interfaces/IManifestation';
import { IUnit } from '../Interfaces/IUnit';
import { ProjectMeasurements } from '../UTMeasurement/ProjectMeasurements';

interface IConditionReportEemuaState {
    isNoDefectElementsVisible: boolean;
}

interface IConditionReportEemuaProps extends ITranslateProps {
    breakDownOpenState: { [key: string]: boolean };
    categories: ICategory[];
    elements: IElement[];
    isAssetDescriptionVisible: boolean;
    isLoadingElements: boolean;
    inspectionStandardType: InspectionStandardTypes;
    constructionYear?: number | undefined;
    surveyId?: string | undefined;
    onExpandAll: () => void;
    onCollapseAll: () => void;
    onChangeBreakdownOpenState: (id: string) => void;
}

class ConditionReportEemuaComponent extends React.Component<IConditionReportEemuaProps, IConditionReportEemuaState> {
    constructor(props: IConditionReportEemuaProps) {
        super(props);

        this.state = {
            isNoDefectElementsVisible: false,
        };
    }

    public override render() {
        const scoreOptions =
            this.props.inspectionStandardType === InspectionStandardTypes.Api570
                ? api570SelectOptions.api570Score
                : defaultSelectOptions.defaultScore;

        return (
            <div className="panel" id="ConditionAssessment">
                <PanelHeading
                    text={
                        this.props.inspectionStandardType === InspectionStandardTypes.Eemua159
                            ? `EEMUA159 ${this.props.t('survey.conditionReport')}`
                            : `API ${this.props.t('survey.conditionReport')}`
                    }
                    icon="fa fa-tasks"
                >
                    <PanelLink
                        href={`/api/surveys/${this.props.surveyId}/apiEemuaExcel`}
                        text="Excel export"
                        isDownload={true}
                        icon="fa fa-download"
                        small={true}
                    />
                </PanelHeading>
                <div className="panel-body" id="reportContent">
                    <PanelButtonLink
                        onClick={() => this.props.onExpandAll()}
                        text={this.props.t('survey.expandAll')}
                        icon="fa fa-plus-square-o"
                        xsmall={true}
                        rightAllign={true}
                    />
                    <PanelButtonLink
                        onClick={() => this.props.onCollapseAll()}
                        text={this.props.t('survey.collapseAll')}
                        icon="fa fa-minus-square-o "
                        xsmall={true}
                        rightAllign={true}
                    />
                    <PanelButtonLink
                        onClick={() => this.setState({ isNoDefectElementsVisible: !this.state.isNoDefectElementsVisible })}
                        text={
                            this.state.isNoDefectElementsVisible
                                ? this.props.t('survey.defectElementsVisible')
                                : this.props.t('survey.allElementsVisible')
                        }
                        icon="fa fa-check-square-o "
                        xsmall={true}
                        rightAllign={true}
                    />

                    {this.props.isLoadingElements && (
                        <table className="table">
                            <thead key="spinner">
                                <tr>
                                    <th className="text-center">
                                        <img src="/assets/img/spinner-ronik.svg" alt="loading" />
                                    </th>
                                </tr>
                            </thead>
                        </table>
                    )}
                    <div id="printview">
                        {this.props.categories.length > 0 && this.props.elements.length > 0 && (
                            <table className="table table-bordered table-condensed">
                                <thead key="start">
                                    <tr className="dark">
                                        <th>{this.props.t('common.code')}</th>
                                        <th>{this.props.t('common.part')}</th>
                                        <th>{this.props.t('common.condition')}</th>
                                        <th>{this.props.t('common.remark')}</th>
                                        <th>&nbsp;</th>
                                    </tr>
                                </thead>

                                {this.props.categories.map(
                                    (category, i) =>
                                        (this.state.isNoDefectElementsVisible || this.categoryHasConditionOrAsset(category.id, category.name)) && (
                                            <React.Fragment key={i}>
                                                <thead>
                                                    <tr className="info">
                                                        <th colSpan={5} className="fw600 fs14">
                                                            {category.name}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {this.props.elements
                                                        .filter((element) => element.categoryId === category.id)
                                                        .map((element) => (
                                                            <React.Fragment key={element.id}>
                                                                {(this.state.isNoDefectElementsVisible ||
                                                                    this.elementHasConditionOrAsset(element)) && (
                                                                    <tr className="fs12" key={element.id}>
                                                                        <td className="fw600">{element.code}</td>
                                                                        <td className="fw600">{element.name}</td>
                                                                        <td>
                                                                            <ConditionScoreLabel
                                                                                value={this.getElementConditionScore(element)}
                                                                                options={scoreOptions}
                                                                            />
                                                                        </td>
                                                                        <td>{element.conditionAdvice}</td>
                                                                        <td className="text-right">
                                                                            {((element.defects && element.defects.length > 0) ||
                                                                                (element.assets && element.assets.length > 0) ||
                                                                                (element.utProjects && element.utProjects.length > 0) ||
                                                                                (element.units && element.units.length > 0)) && (
                                                                                <TableExpandButton
                                                                                    open={this.props.breakDownOpenState[element.id]}
                                                                                    onClick={() => this.props.onChangeBreakdownOpenState(element.id)}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                )}

                                                                {this.renderConditionDetails(element)}

                                                                {element.units.map((unit, unitIndex) => [
                                                                    <React.Fragment key={unit.id}>
                                                                        <tr
                                                                            key={unit.id}
                                                                            className={`${
                                                                                this.props.breakDownOpenState[element.id] ? 'fs12' : 'hidden'
                                                                            } unit-${indexToOddOrEven(unitIndex)}`}
                                                                        >
                                                                            <td>{unit.code}</td>
                                                                            <td>{unit.name}</td>
                                                                            <td>
                                                                                <ConditionScoreLabel value={unit.score} options={scoreOptions} />
                                                                            </td>
                                                                            <td>{unit.conditionAdvice}</td>
                                                                            <td className="text-right condition-button">
                                                                                {((unit.defects && unit.defects.length > 0) ||
                                                                                    (unit.assets && unit.assets.length > 0) ||
                                                                                    (unit.utProjects && unit.utProjects.length > 0) ||
                                                                                    (unit.manifestations && unit.manifestations.length > 0)) && (
                                                                                    <TableExpandButton
                                                                                        open={this.props.breakDownOpenState[unit.id]}
                                                                                        onClick={() => this.props.onChangeBreakdownOpenState(unit.id)}
                                                                                    />
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                        {this.renderConditionDetails(unit)}
                                                                        {unit.manifestations.map((manifestation, manifestationIndex) => (
                                                                            <React.Fragment key={manifestation.id}>
                                                                                <tr
                                                                                    key={manifestation.id}
                                                                                    className={`${
                                                                                        this.props.breakDownOpenState[unit.id] ? 'fs12' : 'hidden'
                                                                                    } manifestation-${indexToOddOrEven(manifestationIndex)}`}
                                                                                >
                                                                                    <td>{manifestation.code}</td>
                                                                                    <td>{manifestation.name}</td>
                                                                                    <td>
                                                                                        <ConditionScoreLabel
                                                                                            value={manifestation.score}
                                                                                            options={scoreOptions}
                                                                                        />
                                                                                    </td>
                                                                                    <td>{manifestation.conditionAdvice}</td>
                                                                                    <td className="text-right condition-button">
                                                                                        {((manifestation.defects &&
                                                                                            manifestation.defects.length > 0) ||
                                                                                            (manifestation.assets &&
                                                                                                manifestation.assets.length > 0) ||
                                                                                            (manifestation.utProjects &&
                                                                                                manifestation.utProjects.length > 0)) && (
                                                                                            <TableExpandButton
                                                                                                open={this.props.breakDownOpenState[manifestation.id]}
                                                                                                onClick={() =>
                                                                                                    this.props.onChangeBreakdownOpenState(
                                                                                                        manifestation.id,
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                                {this.renderConditionDetails(
                                                                                    manifestation,
                                                                                    !this.props.breakDownOpenState[unit.id],
                                                                                )}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </React.Fragment>,
                                                                ])}
                                                            </React.Fragment>
                                                        ))}
                                                </tbody>
                                            </React.Fragment>
                                        ),
                                )}
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderConditionDetails(item: IElement | IUnit | IManifestation, isParentHidden?: boolean): JSX.Element | null {
        if (
            !(
                item.conditionCost ||
                item.conditionAdvice ||
                (item.defects && item.defects.length > 0) ||
                (item.assets && item.assets.length > 0) ||
                (item.utProjects && item.utProjects.length > 0)
            )
        ) {
            return null;
        }
        return (
            <tr className={this.props.breakDownOpenState[item.id] && !isParentHidden ? 'object-table-condition' : 'hidden'} key={`${item.id}cond`}>
                <td colSpan={5}>
                    <div className="dd-content">
                        <div>
                            {item.assets && item.assets.length > 0 && (
                                <PanelResult>
                                    <AssetsViewer assets={item.assets} surveyId={this.props.surveyId} />
                                </PanelResult>
                            )}
                        </div>

                        {/* UtProjects */}
                        {item.utProjects && item.utProjects.length > 0 && (
                            <>
                                {item.utProjects &&
                                    item.utProjects.map((utProject, i) => (
                                        <PanelResult key={i}>
                                            <div key={utProject.id}>
                                                <ProjectMeasurements project={utProject} constructionYear={this.props.constructionYear} />
                                            </div>
                                            {utProject.assets && <AssetsViewer assets={utProject.assets} surveyId={this.props.surveyId} />}
                                        </PanelResult>
                                    ))}
                            </>
                        )}

                        {item.defects && item.defects.length > 0 && (
                            <>
                                {item.defects.length > 0 && (
                                    <PanelResult>
                                        <h4 className="text-primary">{this.props.t('repairAdvice.repairAdvice')}</h4>
                                        <table className="table table-condensed">
                                            <thead>
                                                <tr className="bg-primary">
                                                    <th>{this.props.t('repairAdvice.advice')}</th>
                                                    <th>{this.props.t('repairAdvice.eemua')}</th>
                                                    <th>{this.props.t('repairAdvice.repairDate')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {item.defects.map((defect) => (
                                                    <tr key={defect.id}>
                                                        <td>{defect.description}</td>
                                                        <td>{this.props.t(`repairCategory.${defect.repairAdviceCategory}`)}</td>
                                                        <td>
                                                            <NullableFormattedDate value={defect.repairDate} />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </PanelResult>
                                )}
                            </>
                        )}
                    </div>
                </td>
            </tr>
        );
    }

    private categoryHasConditionOrAsset(categoryId: string, categoryName: string): boolean {
        const elementList = this.props.elements.filter((element) => element.categoryId === categoryId);

        if (categoryName) {
            for (const element of elementList) {
                if (this.elementHasConditionOrAsset(element)) {
                    return true;
                }
            }
        }
        return false;
    }

    private elementHasConditionOrAsset(element: IElement): boolean {
        if (element.score) {
            return true;
        }
        if ((element.assets?.length ?? 0) > 0) {
            return true;
        }
        if (element.utProjects?.length > 0) {
            return true;
        }
        if (element.units.length === 0) {
            return false;
        }

        for (const unit of element.units) {
            if (unit.conditionId) {
                return true;
            }
            if (unit.assets.length > 0) {
                return true;
            }
            if (unit.utProjects.length > 0) {
                return true;
            }
            for (const manifestation of unit.manifestations) {
                if (manifestation.conditionId) {
                    return true;
                }
                if ((manifestation.assets?.length ?? 0) > 0) {
                    return true;
                }
                if (manifestation.utProjects.length > 0) {
                    return true;
                }
            }
        }
        return false;
    }

    private getConditionScoreFromChildren(items: IUnit[] | IManifestation[]) {
        if (items.length === 0) {
            return undefined;
        }

        let scores: (string | undefined)[] = [];
        for (const item of items) {
            if (item.conditionId) {
                scores.push(item.score);
            } else {
                if ((item as IUnit).manifestations) {
                    const childScore = this.getConditionScoreFromChildren((item as IUnit).manifestations);
                    if (childScore) {
                        scores.push(childScore);
                    }
                }
            }
        }
        if (scores.length === 0) {
            return undefined;
        }
        scores = scores.sort().reverse();
        return scores[0];
    }

    // private getUnitConditionScore(unit: IUnit): string {
    //     if (unit.conditionScore) {
    //         return unit.conditionScore;
    //     }
    //     if (unit.manifestations.length === 0) {
    //         return undefined;
    //     }
    //     return this.getConditionScoreFromChildren(unit.manifestations);
    // }

    private getElementConditionScore(element: IElement) {
        if (element.score) {
            return element.score;
        }
        if (element.units.length === 0) {
            return undefined;
        }
        return this.getConditionScoreFromChildren(element.units);
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ConditionReportEEMUA = translate()(ConditionReportEemuaComponent);
